import React, { useState, useMemo } from 'react';
import { useWidgetDefinitions, useAddWidget } from '../../hooks/useDashboard';
import { X, Plus, Check } from 'lucide-react';
import { toast } from '@rickylandino/react-messages';
import Globals from '../../config/globals';

/**
 * WidgetPicker component
 * Modal for selecting and adding widgets to the dashboard
 */
export default function WidgetPicker({ open, onOpenChange, dashboard }) {
const { data: definitions, isLoading } = useWidgetDefinitions();
const addWidget = useAddWidget();
const [selectedCategory, setSelectedCategory] = useState('all');

// Get set of widget definition IDs that are already placed on the dashboard.
// Filter to widgetInstanceKey !== null so available-to-add entries (returned by
// the API alongside placed ones) are not incorrectly marked as "Added".
const addedWidgetIds = useMemo(() => {
    if (!dashboard?.widgets) return new Set();
    return new Set(
        dashboard.widgets
            .filter(w => w.widgetInstanceKey !== null)
            .map(w => w.widgetDefinitionId)
    );
}, [dashboard]);

    if (!open) return null;

    const handleAddWidget = (widgetDefinition) => {
        // Check if widget is already added
        if (addedWidgetIds.has(widgetDefinition.widgetDefinitionId)) {
            toast.info('This widget is already on your dashboard');
            return;
        }

        const request = {
            widgetDefinitionId: widgetDefinition.widgetDefinitionId,
            columnPosition: 0,
            rowPosition: 0,
            widgetTitle: widgetDefinition.widgetName
        };

        addWidget.mutate(request, {
            onSuccess: () => {
                toast.success(`${widgetDefinition.widgetName} added to dashboard`);
                onOpenChange(false);
            },
            onError: () => {
                toast.error('Failed to add widget');
            }
        });
    };

    // Group widgets by category
    const categories = ['all'];
    const widgetsByCategory = {};

    if (definitions) {
        definitions.forEach(def => {
            if (!categories.includes(def.widgetCategory)) {
                categories.push(def.widgetCategory);
            }

            if (!widgetsByCategory[def.widgetCategory]) {
                widgetsByCategory[def.widgetCategory] = [];
            }
            widgetsByCategory[def.widgetCategory].push(def);
        });
    }

    const displayWidgets = selectedCategory === 'all'
        ? definitions || []
        : widgetsByCategory[selectedCategory] || [];

    return (
        <div 
            className="fixed inset-0 z-50 flex items-center justify-center"
            style={{
                backgroundColor: 'rgba(0, 0, 0, 0.5)'
            }}
        >
            <div 
                className="rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col"
                style={{
                    backgroundColor: Globals.theme === 'dark' ? '#2b2b2b' : 'white'
                }}
            >
                {/* Header */}
                <div 
                    className="flex items-center justify-between p-4 border-b"
                    style={{
                        borderColor: Globals.theme === 'dark' ? '#3f3f46' : '#e5e7eb'
                    }}
                >
                    <h2 
                        className="text-xl font-semibold"
                        style={{
                            color: Globals.theme === 'dark' ? '#e4e4e7' : '#18181b'
                        }}
                    >
                        Add Widget
                    </h2>
                    <button
                        onClick={() => onOpenChange(false)}
                        className="p-1 rounded transition-colors"
                        style={{
                            color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = Globals.theme === 'dark' ? '#3f3f46' : '#f4f4f5'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Category Tabs */}
                <div 
                    className="flex gap-2 p-4 border-b overflow-x-auto"
                    style={{
                        borderColor: Globals.theme === 'dark' ? '#3f3f46' : '#e5e7eb'
                    }}
                >
                    {categories.map(category => (
                        <button
                            key={category}
                            onClick={() => setSelectedCategory(category)}
                            className="px-4 py-2 rounded capitalize whitespace-nowrap transition-colors"
                            style={{
                                backgroundColor: selectedCategory === category
                                    ? (Globals.theme === 'dark' ? '#ec4899' : '#db2777')
                                    : (Globals.theme === 'dark' ? '#3f3f46' : '#f4f4f5'),
                                color: selectedCategory === category
                                    ? 'white'
                                    : (Globals.theme === 'dark' ? '#d4d4d8' : '#71717a')
                            }}
                            onMouseEnter={(e) => {
                                if (selectedCategory !== category) {
                                    e.currentTarget.style.backgroundColor = Globals.theme === 'dark' ? '#52525b' : '#e5e7eb';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (selectedCategory !== category) {
                                    e.currentTarget.style.backgroundColor = Globals.theme === 'dark' ? '#3f3f46' : '#f4f4f5';
                                }
                            }}
                        >
                            {category}
                        </button>
                    ))}
                </div>

                {/* Widget Grid */}
                <div className="flex-1 overflow-y-auto p-4">
                    {isLoading ? (
                        <div className="flex items-center justify-center h-full">
                            <div 
                                style={{ 
                                    color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a'
                                }}
                            >
                                Loading widgets...
                            </div>
                        </div>
                    ) : displayWidgets.length === 0 ? (
                        <div className="flex items-center justify-center h-full">
                            <div 
                                style={{ 
                                    color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a'
                                }}
                            >
                                No widgets available
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {displayWidgets.map(widget => {
                                const isAdded = addedWidgetIds.has(widget.widgetDefinitionId);
                                return (
                                    <div
                                        key={widget.widgetDefinitionId}
                                        className="border rounded-lg p-4 transition-all"
                                        style={{
                                            backgroundColor: isAdded 
                                                ? (Globals.theme === 'dark' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(240, 253, 244, 0.8)')
                                                : (Globals.theme === 'dark' ? '#2b2b2b' : 'white'),
                                            borderColor: isAdded 
                                                ? (Globals.theme === 'dark' ? '#15803d' : '#86efac')
                                                : (Globals.theme === 'dark' ? '#ec4899' : '#f9a8d4'),
                                            cursor: isAdded ? 'default' : 'pointer'
                                        }}
                                        onClick={() => !isAdded && handleAddWidget(widget)}
                                    >
                                        <div className="flex items-start justify-between mb-2">
                                            <div className="flex-1">
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                                    <h3 
                                                        className="font-semibold text-sm"
                                                        style={{ 
                                                            color: Globals.theme === 'dark' ? '#e4e4e7' : '#18181b'
                                                        }}
                                                    >
                                                        {widget.widgetName}
                                                    </h3>
                                                    {isAdded && (
                                                        <span 
                                                            style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: '4px',
                                                                fontSize: '0.75rem',
                                                                padding: '2px 8px',
                                                                backgroundColor: Globals.theme === 'dark' ? 'rgba(34, 197, 94, 0.2)' : '#dcfce7',
                                                                color: Globals.theme === 'dark' ? '#4ade80' : '#16a34a',
                                                                borderRadius: '9999px'
                                                            }}
                                                        >
                                                            <Check className="w-3 h-3" />
                                                            Added
                                                        </span>
                                                    )}
                                                </div>
                                                <span 
                                                    className="text-xs px-2 py-0.5 rounded capitalize"
                                                    style={{
                                                        backgroundColor: Globals.theme === 'dark' ? '#3f3f46' : '#f4f4f5',
                                                        color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a'
                                                    }}
                                                >
                                                    {widget.widgetCategory}
                                                </span>
                                            </div>
                                        </div>

                                        <p 
                                            className="text-sm mb-3"
                                            style={{
                                                color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical'
                                            }}
                                        >
                                            {widget.widgetDescription || 'No description available'}
                                        </p>

                                        {!isAdded && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleAddWidget(widget);
                                                }}
                                                disabled={addWidget.isLoading}
                                                className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded transition-colors text-sm font-medium"
                                                style={{
                                                    backgroundColor: Globals.theme === 'dark' ? '#ec4899' : '#db2777',
                                                    color: 'white'
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (!addWidget.isLoading) {
                                                        e.currentTarget.style.backgroundColor = Globals.theme === 'dark' ? '#db2777' : '#be185d';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.backgroundColor = Globals.theme === 'dark' ? '#ec4899' : '#db2777';
                                                }}
                                            >
                                                <Plus className="w-4 h-4" />
                                                Add Widget
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div 
                    className="flex justify-end gap-2 p-4 border-t"
                    style={{
                        borderColor: Globals.theme === 'dark' ? '#3f3f46' : '#e5e7eb'
                    }}
                >
                    <button
                        onClick={() => onOpenChange(false)}
                        className="px-4 py-2 border rounded transition-colors"
                        style={{
                            borderColor: Globals.theme === 'dark' ? '#52525b' : '#d4d4d8',
                            color: Globals.theme === 'dark' ? '#d4d4d8' : '#52525b'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = Globals.theme === 'dark' ? '#3f3f46' : '#f4f4f5'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}
